import { apiClient } from './client';

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

export interface ScriptQuestion {
  id: string;
  question: string;
  type: 'text' | 'yes_no' | 'multiple_choice';
  options?: string[];
  required?: boolean;
}

export interface Campaign {
  id: number;
  uuid: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'completed' | 'draft';
  lead_categories: string[];
  assigned_agent_ids: string[];
  script: ScriptQuestion[];
  scheduled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCampaignRequest {
  name: string;
  description?: string;
  status?: Campaign['status'];
  lead_categories: string[];
  assigned_agent_ids: string[];
  script: ScriptQuestion[];
  scheduled_at?: string | null;
}

export interface UpdateCampaignRequest extends Partial<CreateCampaignRequest> { }

export interface CampaignListRequest {
  page?: number;
  page_size?: number;
  search?: string;
  sort?: Record<string, 'asc' | 'desc'>;
}

export interface ScheduleAiCallRequest {
  campaign_uuid: string;
  agent_id: string;
  scheduled_at: string;
  notes?: string;
}

export interface TriggerAiCallRequest {
  campaign_uuid: string;
  phone_number: string;
  lead_name?: string;
  notes?: string;
}

// ──────────────────────────────────────────────
// Service
// ──────────────────────────────────────────────

class CampaignsService {
  private readonly basePath = '/campaigns';

  async create(data: CreateCampaignRequest): Promise<any> {
    // TODO: replace with real API — return apiClient.post(this.basePath, data)
    return new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            data: {
              uuid: `camp-${Date.now()}`,
              ...data,
              id: Math.floor(Math.random() * 10000),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          }),
        600,
      ),
    );
  }

  async listing(params: CampaignListRequest = {}): Promise<any> {
    // TODO: replace with real API — return apiClient.post(`${this.basePath}/listing`, params)
    const mockCampaigns: Campaign[] = [
      {
        id: 1,
        uuid: 'camp-001',
        name: 'Medicare Outreach Q2',
        description: 'Quarterly outreach campaign for medicare leads',
        status: 'active',
        lead_categories: ['medicare', 'seniors', 'medicare', 'seniors', 'medicare', 'seniors', 'medicare', 'seniors', 'medicare', 'seniors',],
        assigned_agent_ids: ['agent-1', 'agent-2', 'agent-1', 'agent-2', 'agent-1', 'agent-2'],
        script: [
          { id: 'q1', question: 'Are you currently enrolled in Medicare?', type: 'yes_no', required: true },
          { id: 'q2', question: 'What is your primary healthcare concern?', type: 'text', required: false },
        ],
        scheduled_at: '2026-04-15T10:00:00Z',
        created_at: '2026-04-01T08:00:00Z',
        updated_at: '2026-04-01T08:00:00Z',
      },
      {
        id: 2,
        uuid: 'camp-002',
        name: 'Life Insurance Follow-up',
        description: 'Follow-up campaign for life insurance prospects',
        status: 'draft',
        lead_categories: ['life_insurance', 'family'],
        assigned_agent_ids: ['agent-3'],
        script: [
          { id: 'q1', question: 'Do you currently have life insurance?', type: 'yes_no', required: true },
          { id: 'q2', question: 'How many dependents do you have?', type: 'multiple_choice', options: ['0', '1-2', '3-4', '5+'], required: true },
        ],
        scheduled_at: null,
        created_at: '2026-04-02T08:00:00Z',
        updated_at: '2026-04-02T08:00:00Z',
      },
      {
        id: 3,
        uuid: 'camp-003',
        name: 'Dental Plan Awareness',
        description: 'Awareness campaign for dental insurance plans',
        status: 'completed',
        lead_categories: ['dental', 'health'],
        assigned_agent_ids: ['agent-1', 'agent-4'],
        script: [
          { id: 'q1', question: 'Do you have dental coverage currently?', type: 'yes_no', required: true },
        ],
        scheduled_at: '2026-03-01T09:00:00Z',
        created_at: '2026-02-20T08:00:00Z',
        updated_at: '2026-03-10T08:00:00Z',
      },
    ];

    const { search, page = 1, page_size = 10 } = params;
    const filtered = search
      ? mockCampaigns.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.description.toLowerCase().includes(search.toLowerCase()),
      )
      : mockCampaigns;

    return new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            attributes: {
              campaigns: filtered.slice((page - 1) * page_size, page * page_size),
              pagination: { total: filtered.length, page, page_size },
            },
          }),
        400,
      ),
    );
  }

  async getById(uuid: string): Promise<any> {
    // TODO: replace with real API — return apiClient.get(`${this.basePath}/${uuid}`)
    return new Promise((resolve) => setTimeout(() => resolve({ data: null }), 300));
  }

  async update(uuid: string, data: UpdateCampaignRequest): Promise<any> {
    // TODO: replace with real API — return apiClient.put(`${this.basePath}/${uuid}`, data)
    return new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            data: { uuid, ...data, updated_at: new Date().toISOString() },
          }),
        600,
      ),
    );
  }

  async remove(uuid: string): Promise<any> {
    // TODO: replace with real API — return apiClient.delete(`${this.basePath}/${uuid}`)
    return new Promise((resolve) => setTimeout(() => resolve({ data: { success: true } }), 400));
  }

  async scheduleAiCall(data: ScheduleAiCallRequest): Promise<any> {
    // TODO: replace with real API — return apiClient.post(`${this.basePath}/schedule-call`, data)
    return new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            data: {
              id: `schedule-${Date.now()}`,
              ...data,
              status: 'scheduled',
              created_at: new Date().toISOString(),
            },
          }),
        700,
      ),
    );
  }

  /** Fetch available agents for assignment (reuses calling agents listing) */
  async getAvailableAgents(): Promise<any> {
    // TODO: replace with real API — return apiClient.post('/calling-agents/listing', { page: 1, page_size: 100 })
    const mockAgents = [
      { uuid: 'agent-1', display_name: 'Alice Johnson', user: { email: 'alice@example.com' } },
      { uuid: 'agent-2', display_name: 'Bob Smith', user: { email: 'bob@example.com' } },
      { uuid: 'agent-3', display_name: 'Carol White', user: { email: 'carol@example.com' } },
      { uuid: 'agent-4', display_name: 'David Brown', user: { email: 'david@example.com' } },
      { uuid: 'agent-5', display_name: 'Eva Martinez', user: { email: 'eva@example.com' } },
    ];
    return new Promise((resolve) =>
      setTimeout(() => resolve({ attributes: { callingAgents: mockAgents } }), 300),
    );
  }

  /**
   * Fetch campaigns assigned to the currently logged-in calling agent.
   * TODO: replace with real API — return apiClient.get(`${this.basePath}/my-campaigns`)
   */
  async getMyAssignedCampaigns(): Promise<any> {
    const mockAssigned: Campaign[] = [
      {
        id: 1,
        uuid: 'camp-001',
        name: 'Medicare Outreach Q2',
        description: 'Quarterly outreach campaign targeting senior medicare leads',
        status: 'active',
        lead_categories: ['medicare', 'seniors'],
        assigned_agent_ids: ['agent-1', 'agent-2'],
        script: [
          { id: 'q1', question: 'Are you currently enrolled in Medicare?', type: 'yes_no', required: true },
          { id: 'q2', question: 'What is your primary healthcare concern?', type: 'text', required: false },
          { id: 'q3', question: 'Would you like information about supplement plans?', type: 'yes_no', required: false },
        ],
        scheduled_at: '2026-04-15T10:00:00Z',
        created_at: '2026-04-01T08:00:00Z',
        updated_at: '2026-04-01T08:00:00Z',
      },
      {
        id: 3,
        uuid: 'camp-003',
        name: 'Dental Plan Awareness',
        description: 'Awareness campaign for dental insurance plans — warm leads follow-up',
        status: 'active',
        lead_categories: ['dental', 'health'],
        assigned_agent_ids: ['agent-1', 'agent-4'],
        script: [
          { id: 'q1', question: 'Do you have dental coverage currently?', type: 'yes_no', required: true },
          { id: 'q2', question: 'How often do you visit the dentist per year?', type: 'multiple_choice', options: ['Never', 'Once', 'Twice', '3+ times'], required: false },
        ],
        scheduled_at: '2026-04-20T09:00:00Z',
        created_at: '2026-02-20T08:00:00Z',
        updated_at: '2026-03-10T08:00:00Z',
      },
    ];
    return new Promise((resolve) =>
      setTimeout(() => resolve({ attributes: { campaigns: mockAssigned, pagination: { total: mockAssigned.length } } }), 400),
    );
  }

  /**
   * Trigger an immediate AI agent call for a campaign lead.
   * TODO: replace with real API — return apiClient.post(`${this.basePath}/trigger-call`, data)
   */
  async triggerAiCall(data: TriggerAiCallRequest): Promise<any> {
    return new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            data: {
              call_id: `call-${Date.now()}`,
              status: 'initiated',
              phone_number: data.phone_number,
              campaign_uuid: data.campaign_uuid,
              initiated_at: new Date().toISOString(),
            },
          }),
        800,
      ),
    );
  }
}

export const campaignsService = new CampaignsService();
